/**
 * AdminPlansManager - editor de planos no painel admin.
 *
 * Permite editar nome, slogan, preços (AOA/USD), limites, funcionalidades e flags
 * (IA, Ads, multi-utilizador, destaque) dos 4 planos existentes.
 */
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
    Loader2, Save, RotateCcw, Plus, Trash2, Crown,
    Sparkles, Megaphone, Users, Star,
} from "lucide-react";
import { toast } from "sonner";
import { api, getApiErrorMessage } from "@/lib/api";

const NUMERIC_FIELDS = ["price_aoa", "price_usd", "max_channels", "max_posts_per_month"];

export default function AdminPlansManager() {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [original, setOriginal] = useState([]);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/plans");
            setPlans(data);
            setOriginal(JSON.parse(JSON.stringify(data)));
        } catch (e) {
            toast.error(getApiErrorMessage(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const isDirty = JSON.stringify(plans) !== JSON.stringify(original);

    const updatePlan = (idx, key, value) => {
        setPlans((prev) => prev.map((p, i) => {
            if (i !== idx) return p;
            const v = NUMERIC_FIELDS.includes(key)
                ? (value === "" ? 0 : Number(value))
                : value;
            return { ...p, [key]: v };
        }));
    };

    const updateFeature = (idx, fIdx, value) => {
        setPlans((prev) => prev.map((p, i) => {
            if (i !== idx) return p;
            const features = [...(p.features || [])];
            features[fIdx] = value;
            return { ...p, features };
        }));
    };

    const addFeature = (idx) => {
        setPlans((prev) => prev.map((p, i) =>
            i === idx ? { ...p, features: [...(p.features || []), ""] } : p
        ));
    };

    const removeFeature = (idx, fIdx) => {
        setPlans((prev) => prev.map((p, i) =>
            i === idx ? { ...p, features: (p.features || []).filter((_, j) => j !== fIdx) } : p
        ));
    };

    const reset = () => {
        setPlans(JSON.parse(JSON.stringify(original)));
        toast.info("Alterações descartadas");
    };

    const save = async () => {
        // Validação simples
        for (const p of plans) {
            if (!p.id?.trim()) { toast.error("Cada plano deve ter um ID"); return; }
            if (!p.name?.trim()) { toast.error(`Plano '${p.id}' precisa de um nome`); return; }
            if (Number(p.price_aoa) < 0 || Number(p.price_usd) < 0) {
                toast.error("Preços não podem ser negativos");
                return;
            }
        }
        setSaving(true);
        try {
            // Limpa features vazias
            const cleaned = plans.map((p) => ({
                ...p,
                features: (p.features || []).map((f) => (f || "").trim()).filter(Boolean),
            }));
            const { data } = await api.put("/admin/plans", cleaned);
            setPlans(data.plans);
            setOriginal(JSON.parse(JSON.stringify(data.plans)));
            toast.success("Planos atualizados! Os preços novos já estão visíveis para os clientes.");
        } catch (e) {
            toast.error(getApiErrorMessage(e));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Card className="p-6 rounded-2xl bg-card border-border flex items-center justify-center text-muted-foreground">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> A carregar planos…
            </Card>
        );
    }

    return (
        <div className="space-y-4" data-testid="admin-plans-manager">
            <Card className="p-4 rounded-2xl bg-[#6B21D9]/8 border-[#6B21D9]/20">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <h3 className="font-display text-lg font-semibold flex items-center gap-2">
                            <Crown className="h-5 w-5 text-[#A78BFA]" /> Editar Planos
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                            Aqui pode alterar preços, descrições e funcionalidades de todos os planos.
                            As alterações ficam <strong>visíveis imediatamente</strong> no site público (Pricing, Landing) e na Área Cliente. Os descontos por período (Mensal/Trimestral/Semestral/Anual) ajustam-se automaticamente aos novos preços base.
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={reset}
                            disabled={!isDirty || saving}
                            data-testid="admin-plans-reset-button"
                        >
                            <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Descartar
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            onClick={save}
                            disabled={!isDirty || saving}
                            className="btn-grad-purple text-white"
                            data-testid="admin-plans-save-button"
                        >
                            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                            Guardar todos
                        </Button>
                    </div>
                </div>
            </Card>

            <div className="grid lg:grid-cols-2 gap-4">
                {plans.map((p, idx) => (
                    <Card
                        key={p.id || idx}
                        className={`p-5 rounded-2xl bg-card relative ${
                            p.highlight ? "border-[#6B21D9] ring-1 ring-[#6B21D9]/30" : "border-border"
                        }`}
                        data-testid={`admin-plan-card-${p.id}`}
                    >
                        {p.highlight && (
                            <Badge className="absolute -top-3 left-5 btn-grad-purple text-white border-none gap-1">
                                <Sparkles className="h-3 w-3" /> Destacado
                            </Badge>
                        )}
                        <div className="flex items-start justify-between gap-2 mb-3">
                            <div className="flex-1">
                                <div className="text-[11px] text-muted-foreground mb-1">ID: <span className="font-mono">{p.id}</span></div>
                                <Input
                                    value={p.name || ""}
                                    onChange={(e) => updatePlan(idx, "name", e.target.value)}
                                    className="font-display text-lg font-semibold !text-lg !h-9"
                                    placeholder="Nome"
                                    data-testid={`admin-plan-${p.id}-name`}
                                />
                            </div>
                        </div>

                        <Label className="text-xs">Slogan</Label>
                        <Input
                            value={p.tagline || ""}
                            onChange={(e) => updatePlan(idx, "tagline", e.target.value)}
                            placeholder="Slogan curto"
                            className="mb-4"
                            data-testid={`admin-plan-${p.id}-tagline`}
                        />

                        <div className="grid grid-cols-2 gap-3 mb-4">
                            <div>
                                <Label className="text-xs">Preço AOA (mensal)</Label>
                                <Input
                                    type="number"
                                    min="0"
                                    value={p.price_aoa ?? 0}
                                    onChange={(e) => updatePlan(idx, "price_aoa", e.target.value)}
                                    data-testid={`admin-plan-${p.id}-price-aoa`}
                                />
                            </div>
                            <div>
                                <Label className="text-xs">Preço USD (mensal)</Label>
                                <Input
                                    type="number"
                                    min="0"
                                    value={p.price_usd ?? 0}
                                    onChange={(e) => updatePlan(idx, "price_usd", e.target.value)}
                                    data-testid={`admin-plan-${p.id}-price-usd`}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 mb-4">
                            <div>
                                <Label className="text-xs">Máx. canais (-1 = ilimitado)</Label>
                                <Input
                                    type="number"
                                    value={p.max_channels ?? -1}
                                    onChange={(e) => updatePlan(idx, "max_channels", e.target.value)}
                                    data-testid={`admin-plan-${p.id}-max-channels`}
                                />
                            </div>
                            <div>
                                <Label className="text-xs">Máx. posts/mês (-1 = ilimitado)</Label>
                                <Input
                                    type="number"
                                    value={p.max_posts_per_month ?? -1}
                                    onChange={(e) => updatePlan(idx, "max_posts_per_month", e.target.value)}
                                    data-testid={`admin-plan-${p.id}-max-posts`}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 mb-4">
                            <ToggleRow
                                icon={Sparkles}
                                label="IA Generativa"
                                value={!!p.has_ai}
                                onChange={(v) => updatePlan(idx, "has_ai", v)}
                                testId={`admin-plan-${p.id}-has-ai`}
                            />
                            <ToggleRow
                                icon={Megaphone}
                                label="Ads / Marketplace"
                                value={!!p.has_ads}
                                onChange={(v) => updatePlan(idx, "has_ads", v)}
                                testId={`admin-plan-${p.id}-has-ads`}
                            />
                            <ToggleRow
                                icon={Users}
                                label="Multi-utilizador"
                                value={!!p.multi_user}
                                onChange={(v) => updatePlan(idx, "multi_user", v)}
                                testId={`admin-plan-${p.id}-multi-user`}
                            />
                            <ToggleRow
                                icon={Star}
                                label="Destaque (Mais escolhido)"
                                value={!!p.highlight}
                                onChange={(v) => updatePlan(idx, "highlight", v)}
                                testId={`admin-plan-${p.id}-highlight`}
                            />
                        </div>

                        {/* Features */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <Label className="text-xs">Funcionalidades ({(p.features || []).length})</Label>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => addFeature(idx)}
                                    data-testid={`admin-plan-${p.id}-add-feature`}
                                >
                                    <Plus className="h-3.5 w-3.5 mr-1" /> Adicionar
                                </Button>
                            </div>
                            <div className="space-y-1.5">
                                {(p.features || []).map((f, fIdx) => (
                                    <div key={fIdx} className="flex items-center gap-1.5">
                                        <Input
                                            value={f}
                                            onChange={(e) => updateFeature(idx, fIdx, e.target.value)}
                                            placeholder="Funcionalidade…"
                                            className="text-sm h-9"
                                            data-testid={`admin-plan-${p.id}-feature-${fIdx}`}
                                        />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="h-9 w-9 text-muted-foreground hover:text-[#FB7185]"
                                            onClick={() => removeFeature(idx, fIdx)}
                                            data-testid={`admin-plan-${p.id}-remove-feature-${fIdx}`}
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    </div>
                                ))}
                                {(p.features || []).length === 0 && (
                                    <p className="text-xs text-muted-foreground italic">Sem funcionalidades. Clique em "Adicionar".</p>
                                )}
                            </div>
                        </div>
                    </Card>
                ))}
            </div>

            {isDirty && (
                <div className="sticky bottom-4 z-20">
                    <Card className="p-3 rounded-xl bg-[#6B21D9]/95 backdrop-blur border-[#6B21D9] flex items-center justify-between text-white">
                        <span className="text-sm font-medium">Tem alterações não guardadas</span>
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={reset} className="bg-white/10 border-white/20 text-white hover:bg-white/20">
                                Descartar
                            </Button>
                            <Button size="sm" onClick={save} disabled={saving} className="bg-white text-[#6B21D9] hover:bg-white/90">
                                {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                                Guardar
                            </Button>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
}

function ToggleRow({ icon: Icon, label, value, onChange, testId }) {
    return (
        <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-border bg-secondary/30">
            <div className="flex items-center gap-2 text-sm">
                <Icon className="h-3.5 w-3.5 text-[#A78BFA]" />
                <span>{label}</span>
            </div>
            <Switch checked={value} onCheckedChange={onChange} data-testid={testId} />
        </div>
    );
}
